import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery, type QueryKey } from '@tanstack/react-query';
import { useCurrentSchool } from './useCurrentSchool';

/** How long typing must pause before a search term is sent. */
export const SEARCH_DEBOUNCE_MS = 300;

export interface ApiSearchOptions {
  /** Pause after the last keystroke before the term is sent; defaults to {@link SEARCH_DEBOUNCE_MS}. */
  debounceMs?: number;
  /** Keep showing the last results (not a loading state) while a new term waits and loads. */
  keepPrevious?: boolean;
}

/**
 * For custom editors that need the complete matching list, rather than a table page.
 * `skipEmpty`: the caller already holds the unfiltered list, so nothing is fetched until a term is typed.
 * Each term is its own query and an outdated one is aborted, so a slow answer never replaces a newer term's.
 */
export function useApiSearch<T>(fetch: (schoolId: number, params: Record<string, unknown>, signal?: AbortSignal) => Promise<T>, key: QueryKey, search: string, params: Record<string, unknown> = {}, enabled = true, organizationWide = false, skipEmpty = false, options: ApiSearchOptions = {}) {
  const { debounceMs = SEARCH_DEBOUNCE_MS, keepPrevious = false } = options;
  const current = useCurrentSchool();
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    if (search.trim() === debounced) return;
    const timer = setTimeout(() => setDebounced(search.trim()), debounceMs);
    return () => clearTimeout(timer);
  }, [search, debounced, debounceMs]);
  const request = { ...params, search: debounced || undefined };
  const query = useQuery({
    queryKey: [...key, 'search', organizationWide ? null : current.currentSchoolId, request],
    queryFn: ({ signal }) => fetch(current.currentSchoolId ?? 0, request, signal),
    enabled: enabled && (!skipEmpty || debounced !== '') && (organizationWide || current.currentSchoolId !== null),
    // Only the same school's last results stand in; another school's never show.
    placeholderData: keepPrevious
      ? (previous, previousQuery) =>
          previousQuery?.queryKey[key.length + 1] === (organizationWide ? null : current.currentSchoolId)
            ? keepPreviousData(previous)
            : undefined
      : undefined,
  });
  // Loading: the first load of a term, or a typed term still waiting out the debounce - not a background refetch after a save.
  const waiting = search.trim() !== debounced && !(keepPrevious && query.data !== undefined);
  return { ...query, isLoading: query.isLoading || waiting || (!organizationWide && current.currentSchoolId === null && current.isLoading), error: query.error ?? (!organizationWide && current.currentSchoolId === null ? current.error : null) };
}
